import { ReleaseNotesService } from '@src/services/ReleaseNotesService';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, runInAction } from 'mobx';

import { extractErrorInfo } from '../utility/error';

import type { RootStore } from './RootStore';
import type { ProcessedRelease } from '@src/services/ReleaseNotesService';

const logger = LogManager.getLogger('store:WhatsNewStore');

const LAST_SEEN_KEY = 'arbor:whatsNew:lastSeenId';

export class WhatsNewStore {
  latestRelease: ProcessedRelease | null = null;
  allReleases: ProcessedRelease[] = [];
  isDrawerOpen: boolean = false;
  isLoading: boolean = false;
  error: string | null = null;
  lastSeenReleaseId: string | null = null;

  private rootStore: RootStore;
  private allReleasesSkip: number = 0;
  private totalCount: number = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.lastSeenReleaseId = this.readLastSeen();

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get hasNewRelease(): boolean {
    if (!this.latestRelease) return false;
    return this.latestRelease.id !== this.lastSeenReleaseId;
  }

  get canLoadMore(): boolean {
    return this.allReleases.length < this.totalCount && !this.isLoading;
  }

  /** When opening the drawer, show the latest release view (even if already seen). */
  isShowingNewRelease: boolean = false;

  openDrawer(forceLatest = false): void {
    this.isShowingNewRelease = forceLatest || this.hasNewRelease;
    this.isDrawerOpen = true;
    this.markAsSeen();
  }

  closeDrawer(): void {
    this.isDrawerOpen = false;
  }

  markAsSeen(): void {
    if (!this.latestRelease) return;
    this.lastSeenReleaseId = this.latestRelease.id;
    try {
      localStorage.setItem(LAST_SEEN_KEY, this.latestRelease.id);
    } catch {
      logger.warn('Failed to write lastSeenReleaseId to localStorage');
    }
  }

  private getRepository() {
    return this.rootStore.datoClientStore.releasesRepository;
  }

  async fetchLatest(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.error = null;

    try {
      const release = await this.getRepository().fetchLatestRelease();
      runInAction(() => {
        this.latestRelease = release
          ? ReleaseNotesService.processRelease(release)
          : null;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = 'Failed to load release notes';
        this.isLoading = false;
      });
      logger.error('Failed to fetch latest release', extractErrorInfo(err));
    }
  }

  async fetchAll(): Promise<void> {
    if (this.isLoading) return;
    if (this.allReleases.length > 0 && !this.canLoadMore) return;

    this.isLoading = true;
    this.error = null;

    try {
      const { releases, totalCount } = await this.getRepository().fetchReleases(
        this.allReleasesSkip,
        10,
      );
      runInAction(() => {
        const processed = releases.map(r =>
          ReleaseNotesService.processRelease(r),
        );
        this.allReleases = [...this.allReleases, ...processed];
        this.allReleasesSkip += releases.length;
        this.totalCount = totalCount;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = 'Failed to load release history';
        this.isLoading = false;
      });
      logger.error('Failed to fetch all releases', extractErrorInfo(err));
    }
  }

  private readLastSeen(): string | null {
    try {
      return localStorage.getItem(LAST_SEEN_KEY);
    } catch {
      return null;
    }
  }
}
